import axios, { AxiosInstance } from 'axios';
import { useAuth } from '@clerk/clerk-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class ApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  setAuthToken(token: string | null) {
    if (token) {
      console.log('Setting auth token, length:', token.length);
      this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      console.log('Clearing auth token');
      delete this.axiosInstance.defaults.headers.common['Authorization'];
    }
  }

  // User endpoints
  async getCurrentUser() {
    // This endpoint doesn't exist in the backend
    // User info should come from Clerk directly
    throw new Error('Use Clerk hooks to get current user info');
  }

  // Configuration endpoints
  async generateConfiguration(data: {
    switch_name: string;
    vendor: string;
    vlan_id: number;
    ip_address: string;
    subnet_mask: string;
    gateway: string;
    template_id?: string;
  }) {
    const response = await this.axiosInstance.post('/api/configurations/generate', data);
    return response.data;
  }

  async generateManualConfiguration(data: {
    template_id?: string;
    vendor: string;
    switch_type: string;
    parameters: Record<string, any>;
    validate?: boolean;
  }) {
    const response = await this.axiosInstance.post('/api/configurations/generate-manual', data);
    return response.data;
  }

  async generateFromNaturalLanguage(request: string) {
    const response = await this.axiosInstance.post('/api/configurations/generate', {
      natural_language_input: request,
      vendor: 'cisco',  // Default, can be made dynamic
      switch_type: 'edge'  // Default, can be made dynamic
    });
    return response.data;
  }

  async saveConfiguration(data: {
    switch_name: string;
    vendor: string;
    switch_type?: string;
    configuration: string;
    template_id?: string;
    template_name?: string;
    natural_language_request?: string;
    parsed_parameters?: any;
    manual?: boolean;
  }) {
    // Note: Configurations are already saved when generated via /api/configurations/generate
    // This endpoint doesn't exist in the backend currently
    // For now, just return success since the config was already saved during generation
    return { success: true, message: 'Configuration already saved during generation' };
  }

  async listConfigurations() {
    const response = await this.axiosInstance.get('/api/configurations/');
    return response.data;
  }

  async getConfiguration(id: string) {
    const response = await this.axiosInstance.get(`/api/configurations/${id}`);
    return response.data;
  }

  async validateConfiguration(config: any) {
    const response = await this.axiosInstance.post('/api/validate/config', config);
    return response.data;
  }

  // Template endpoints
  async listTemplates() {
    const response = await this.axiosInstance.get('/api/templates/');
    return response.data;
  }

  async getTemplate(templateId: string) {
    const response = await this.axiosInstance.get(`/api/templates/${templateId}`);
    return response.data;
  }

  async getTemplatesByType(vendor: string, switchType: string) {
    const response = await this.axiosInstance.get('/api/templates/', {
      params: {
        vendor: vendor,
        switch_type: switchType
      }
    });
    return response.data;
  }

  async createTemplate(data: {
    name: string;
    vendor: string;
    switch_type: string;
    version: string;
    description?: string;
    template_content: string;
  }) {
    const response = await this.axiosInstance.post('/api/templates/', data);
    return response.data;
  }

  async updateConfiguration(id: string, data: { configuration: string }) {
    // Note: There's no PUT endpoint for configurations in the backend
    // Configurations are immutable once created
    throw new Error('Configuration updates not supported - create a new configuration instead');
  }

  async deleteConfiguration(id: string) {
    const response = await this.axiosInstance.delete(`/api/configurations/${id}`);
    return response.data;
  }

  // Health check
  async healthCheck() {
    const response = await this.axiosInstance.get('/health');
    return response.data;
  }
}

// Create a singleton instance
const apiService = new ApiService();

// Custom hook to use API with Clerk authentication
export const useApi = () => {
  const { getToken } = useAuth();

  const getAuthenticatedApi = async () => {
    try {
      // Get the session token from Clerk
      const token = await getToken();
      console.log('Got token from Clerk:', token ? 'Token exists' : 'No token');
      apiService.setAuthToken(token);
      return apiService;
    } catch (error) {
      console.error('Error getting token from Clerk:', error);
      // Return API without token for public endpoints
      return apiService;
    }
  };

  return {
    api: apiService,
    getAuthenticatedApi,
  };
};

export default apiService;